import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TabsContent, toast } from 'design-system';
import { DownloadIcon, Maximize2Icon, Minimize2Icon, PlusIcon, Share2Icon } from 'lucide-react';

import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import type { ChartType } from '@/features/charts/types/highcharts';
// oxlint-disable-next-line gx-boundaries/no-cross-feature-import -- Reuse Plan 02's public export callbacks instead of duplicating export orchestration.
import { useCsvWidgetExportAction, useExportChartAction } from '@/features/data-hub';
// oxlint-disable-next-line gx-boundaries/no-cross-feature-import -- Preserve the existing selector-mode conflict contract during this shell-only migration.
import { MODE_CONFLICT_MESSAGES, SELECTOR_MODES } from '@/features/symbol-selector';
import { FeatureFlag } from '@/services/feature-flags';
import { useFlag } from '@/services/feature-flags/react';
import { useAppSelector } from '@/store/hooks';

import { useBlockTabNavigation } from '../../hooks/useBlockTabNavigation';
import { getMarketBlockTabParam } from '../../lib/getMarketBlockTabParam';
import type { BaseTabType } from '../../types';
import { ShareBlockNames } from '../../types';
import { useAddToDashboardAction } from '../AddToDashboardButton/useAddToDashboardAction';
import { hasOtherGroupSymbols } from '../CommoditiesTable/useOtherGroupData';
import { useMarketAppControls } from '../contexts/MarketAppControlsContext';
import { useMarketWidget } from '../contexts/MarketWidgetContext';
import { MarketWidget } from '../MarketWidget';
import MarketWidgetSurface, { type MarketWidgetAction } from '../MarketWidgetSurface';
import { useShareBlockAction } from '../Share/ShareBlock/useShareBlockAction';
import TabExportButton, { isTabExportSupported } from '../TabExportButton';

type TabsContainerProps = {
  tabs: BaseTabType[];
  layout?: { fullWidth?: boolean; height?: number };
  blockIndex: number;
};

const getExportChartType = (tab: BaseTabType): ChartType => {
  if (tab.type !== 'graph') return 'spot';

  const graphType = tab.metadata?.graphType;
  if (graphType === 'spot' || graphType === 'forwardSimple' || graphType === 'seasonal') {
    return graphType;
  }

  return 'spot';
};

const TabsContainer = ({ tabs, blockIndex }: TabsContainerProps) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const exportControlRef = useRef<HTMLDivElement>(null);
  const { showShareBlock, showAddToDashboard } = useMarketAppControls();
  const selectorMode = useAppSelector(state => state.symbolSelector.selectorMode);
  const activeTabData = tabs[activeTab];
  const isModeIncompatible =
    selectorMode === SELECTOR_MODES.FORWARD_CURVE ||
    (activeTabData.type === 'graph' && selectorMode === SELECTOR_MODES.SPOT);
  const modeConflictMessage =
    selectorMode === SELECTOR_MODES.FORWARD_CURVE || selectorMode === SELECTOR_MODES.SPOT
      ? MODE_CONFLICT_MESSAGES[selectorMode]
      : undefined;

  const handleModeConflictClick = useCallback(() => {
    if (!modeConflictMessage) return;

    toast.add({ title: modeConflictMessage, type: 'warning', timeout: 10000 });
  }, [modeConflictMessage]);

  const { isFullScreen, setIsFullScreen, setFullScreenAvailable, fullScreenAvailable } =
    useMarketWidget();

  const isPersonalizationEnabled = useFlag(FeatureFlag.PersonalizationEnabled);
  const isIndexOnlyTable =
    activeTabData.type === 'table' && hasOtherGroupSymbols(activeTabData.sections);

  const { hasPermission } = usePermission();
  const isExportViewPermissionGranted = hasPermission(Permission.ExportView);
  const isExportImagePermissionGranted = hasPermission(Permission.ExportImage);
  const isFullScreenPermissionGranted = hasPermission(Permission.ViewChartInFullscreen);

  const visibleTabs = tabs;
  const blockName: ShareBlockNames = useMemo(() => {
    const tab = visibleTabs[activeTab];
    switch (tab.type) {
      case 'table':
        return ShareBlockNames.Table;
      case 'graph':
        if (tab.metadata?.graphType === 'custom') {
          return ShareBlockNames.CustomChart;
        }
        if (tab.metadata?.graphType === 'static') {
          return ShareBlockNames.CustomChart;
        }
        if (tab.metadata?.graphType === 'seasonal') {
          return ShareBlockNames.SeasonalChart;
        }
        if (tab.metadata?.graphType === 'forwardSimple') {
          return ShareBlockNames.ForwardCurveChart;
        }
        return ShareBlockNames.SpotChart;
      case 'feed':
        return ShareBlockNames.Feed;
      case 'minitable':
        return ShareBlockNames.Tables;
      case 'spot_history_table':
        return ShareBlockNames.Table;
      case 'csvtable':
        return ShareBlockNames.CSVTable;
      case 'file_list':
        return ShareBlockNames.Files;
      default:
        return ShareBlockNames.Table;
    }
  }, [activeTab, visibleTabs]);

  const isFullscreenAvailable = useCallback(
    (tabIndex: number) => {
      const tab = visibleTabs[tabIndex];
      let available = false;
      if (tab.type === 'graph') {
        const graphType = tab.metadata?.graphType;
        available =
          graphType !== 'custom' && graphType !== 'seasonal' && graphType !== 'forwardSimple';
      }
      return available;
    },
    [visibleTabs]
  );

  useEffect(() => {
    setFullScreenAvailable(isFullscreenAvailable(activeTab));
  }, [activeTab, setFullScreenAvailable, isFullscreenAvailable]);

  const shareTabParam = useMemo(
    () => getMarketBlockTabParam(blockName, blockIndex, visibleTabs[activeTab].name),
    [blockIndex, blockName, activeTab, visibleTabs]
  );

  const tabsWithDisabled = useMemo(
    () =>
      tabs.map((tab, index) => ({
        ...tab,
        disabled: isFullScreen && !isFullscreenAvailable(index)
      })),
    [tabs, isFullScreen, isFullscreenAvailable]
  );
  const canAddActiveTabToDashboard =
    activeTabData.type !== 'csvtable' &&
    activeTabData.type !== 'multi_group_table' &&
    activeTabData.type !== 'monthly_recap' &&
    activeTabData.metadata?.graphType !== 'custom' &&
    (activeTabData.type !== 'spot_history_table' || Boolean(activeTabData.items));

  const addToDashboardAction = useAddToDashboardAction(activeTabData);
  const shareBlockAction = useShareBlockAction({
    activeTabName: activeTabData.name,
    blockName,
    shareTabParam
  });
  const { disabled: isChartExportDisabled, exportChart } = useExportChartAction({
    graphType: getExportChartType(activeTabData),
    isFullScreen,
    metadata: activeTabData.metadata,
    tabName: activeTabData.name
  });
  const { disabled: isCsvExportDisabled, exportCsv } = useCsvWidgetExportAction({
    tabName: activeTabData.name
  });
  const isCustomGraph =
    activeTabData.type === 'graph' && activeTabData.metadata?.graphType === 'custom';
  const isExportSupported = isTabExportSupported(activeTabData);
  const usesExistingExportControl =
    isExportSupported &&
    (isCustomGraph || (activeTabData.type !== 'graph' && activeTabData.type !== 'csvtable'));
  let isExportActionDisabled = false;
  if (activeTabData.type === 'graph') {
    isExportActionDisabled = isCustomGraph
      ? !isExportImagePermissionGranted
      : isChartExportDisabled;
  } else if (activeTabData.type === 'csvtable') {
    isExportActionDisabled = isCsvExportDisabled;
  }

  const actions = useMemo<MarketWidgetAction[]>(
    () => [
      {
        id: 'add-to-dashboard',
        label: 'Add to dashboard',
        icon: <PlusIcon />,
        visible:
          !isIndexOnlyTable &&
          canAddActiveTabToDashboard &&
          isPersonalizationEnabled &&
          showAddToDashboard,
        locked: isModeIncompatible,
        lockedReason: isModeIncompatible ? modeConflictMessage : undefined,
        disabled: addToDashboardAction.disabled,
        onSelect: addToDashboardAction.openFrom
      },
      {
        id: 'share',
        label: 'Share',
        icon: <Share2Icon />,
        visible: showShareBlock && activeTabData.type !== 'monthly_recap',
        locked: isModeIncompatible,
        lockedReason: isModeIncompatible ? modeConflictMessage : undefined,
        onSelect: shareBlockAction.openFrom
      },
      {
        id: 'export',
        label: 'Export',
        icon: <DownloadIcon />,
        visible: isExportViewPermissionGranted && isExportSupported,
        locked: isModeIncompatible,
        lockedReason: isModeIncompatible ? modeConflictMessage : undefined,
        disabled: isExportActionDisabled,
        onSelect: () => {
          if (activeTabData.type === 'graph' && !isCustomGraph) {
            exportChart();
            return;
          }
          if (activeTabData.type === 'csvtable') {
            exportCsv();
            return;
          }

          // TabExportButton owns each tab kind's existing export callback. None
          // opens anchor-positioned UI, so this click retains focus on the
          // stable visible MarketWidgetSurface action that initiated it.
          exportControlRef.current?.querySelector('button')?.click();
        }
      },
      {
        id: 'fullscreen',
        label: isFullScreen ? 'Close full screen' : 'Full screen',
        icon: isFullScreen ? <Minimize2Icon /> : <Maximize2Icon />,
        visible: isFullScreenPermissionGranted && (isFullScreen || fullScreenAvailable),
        onSelect: () => {
          setIsFullScreen(previous => !previous);
        }
      }
    ],
    [
      activeTabData,
      addToDashboardAction.disabled,
      addToDashboardAction.openFrom,
      canAddActiveTabToDashboard,
      exportChart,
      exportCsv,
      fullScreenAvailable,
      isExportViewPermissionGranted,
      isExportSupported,
      isFullScreen,
      isFullScreenPermissionGranted,
      isIndexOnlyTable,
      isCustomGraph,
      isExportActionDisabled,
      isModeIncompatible,
      isPersonalizationEnabled,
      modeConflictMessage,
      setIsFullScreen,
      shareBlockAction.openFrom,
      showAddToDashboard,
      showShareBlock
    ]
  );

  const { blockRef } = useBlockTabNavigation(visibleTabs, setActiveTab);

  return (
    <div
      ref={blockRef}
      className='h-full'
    >
      <MarketWidgetSurface
        actions={actions}
        activeTab={activeTab}
        fullscreen={isFullScreen}
        tabs={tabsWithDisabled}
        title={activeTabData.name}
        onLockedAction={handleModeConflictClick}
        onRequestCloseFullscreen={() => {
          setIsFullScreen(false);
        }}
        onTabChange={setActiveTab}
      >
        {visibleTabs.map((tab, index) => (
          <TabsContent
            key={tab.id}
            value={String(index)}
          >
            <MarketWidget tab={tab} />
          </TabsContent>
        ))}
      </MarketWidgetSurface>
      {addToDashboardAction.layer}
      {shareBlockAction.layer}
      {usesExistingExportControl ? (
        <div
          ref={exportControlRef}
          aria-hidden='true'
          hidden
        >
          <TabExportButton tab={activeTabData} />
        </div>
      ) : null}
    </div>
  );
};

export default TabsContainer;
